<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { z } from 'zod'

import {
  BlockContainer,
  Button,
  CheckboxField,
  FormErrors,
  Heading,
  SelectField,
  TextareaField,
  TextField,
  useForm
} from '@toni/brand-ui'

import { usePageStore } from '../../stores/page'
import { useConfigStore } from '../../stores/config'
import { usePageBlockWidth } from '../../composables/use-page-block-width.ts'

const { t } = useI18n()

const schema = z.object({
  name: z.string({ required_error: t('contactForm.errors.required') })
    .nonempty(t('contactForm.errors.required')),
  email: z.string({ required_error: t('contactForm.errors.required') })
    .nonempty(t('contactForm.errors.required'))
    .email(t('contactForm.errors.email')),
  subject: z.enum(['technical', 'content'], {
    errorMap: () => ({ message: t('contactForm.errors.subject') })
  }),
  message: z.string({ required_error: t('contactForm.errors.required') })
    .nonempty(t('contactForm.errors.required')),
  accept: z.boolean({ required_error: t('contactForm.errors.consent.full') })
    .refine((val) => val === true, {
      message: t('contactForm.errors.consent.full')
    })
})

const { handleSubmit, errors, submitCount } = useForm({
  schema
})

const subjectOptions = [
  { label: 'Technische Rückmeldung', value: 'technical' },
  { label: 'Inhaltliche Rückmeldung', value: 'content' }
]

const brand = usePageStore().brandFromStore
const configStore = useConfigStore()

const success = ref(false)

const onSubmit = handleSubmit(async (values: z.infer<typeof schema>) => {
  try {
    const url = new URL('/v1/support/email', configStore.rsConfig.orchestrationServiceUrl)
    await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        brand,
        actualEmail: values.email,
        name: values.name,
        subject: values.subject,
        message: values.message
      })
    })

    success.value = true
  } catch (error) {
    console.error(error)
    success.value = false
  }
})
const isHome = computed(() => usePageStore().isHome as boolean)
const size = computed(() => usePageBlockWidth(usePageStore().pageData?.pageMeta, isHome.value, brand.value).value)
</script>

<template>
  <BlockContainer :size="size">
    <div v-if="success"
         class="bg-highlight-tertiary-background p-8 space-y-6">
      <Heading variant="h4"
               class="pt-4">
        {{ $t('contactForm.success.title') }}
      </Heading>
      <p class="text-ui-lg font-ui">
        {{ $t('contactForm.success.message') }}
      </p>
      <Button variant="primary"
              @click="success = false">
        {{ $t('contactForm.success.newEnquiry') }}
      </Button>
    </div>
    <form v-else
          class="bg-highlight-tertiary-background p-8 space-y-4"
          @submit="onSubmit">
      <FormErrors v-if="submitCount > 0"
                  :title="$t('contactForm.errors.count', { count: Object.keys(errors).length })"
                  :errors="errors" />
      <TextField
        name="name"
        :label="$t('contactForm.labels.name')"
        :placeholder="$t('contactForm.placeholders.name')" />
      <TextField
        name="email"
        :label="$t('contactForm.labels.email')"
        :placeholder="$t('contactForm.placeholders.email')" />
      <SelectField
        name="subject"
        :label="$t('contactForm.labels.subject')"
        :placeholder="$t('contactForm.placeholders.subject')"
        :options="subjectOptions" />
      <TextareaField
        name="message"
        :label="$t('contactForm.labels.message')"
        :placeholder="$t('contactForm.placeholders.message')" />
      <CheckboxField name="accept">
        {{ $t('contactForm.consent.prefix') }}
        <a href="/datenschutz"
           class="text-aside-foreground underline">{{ $t('contactForm.consent.privacy') }}</a>
        {{ $t('contactForm.consent.conjunction') }}
        <a href="/nutzungsbedingungen"
           class="text-aside-foreground underline">{{ $t('contactForm.consent.terms') }}</a>
      </CheckboxField>
      <Button variant="primary"
              type="submit">
        {{ $t('contactForm.labels.submit') }}
      </Button>
    </form>
  </BlockContainer>
</template>
